import { parseSafeDate } from '@/utils/dateNormalize';
import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { format, subDays, startOfMonth, endOfMonth, startOfDay, endOfDay, startOfWeek, endOfWeek } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Plus, Search, Filter, FileText, Package, Check, X, AlertCircle, TrendingUp, TrendingDown, ArrowUpRight, DollarSign, Calendar as CalendarIcon, FileSpreadsheet, Eye, Download, Pencil, Trash2, Clock, Upload, Loader2, Link as LinkIcon, RefreshCcw, SlidersHorizontal, ChevronUp, ChevronDown, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import useGranularPermissions from '../hooks/useGranularPermissions';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { LineChart, Line, ResponsiveContainer } from 'recharts';
import { Input } from '../components/ui/input';
import { toast } from 'sonner';
import ExpensePharmacyFilter from '../components/ExpensePharmacyFilter';
import ExpenseDateRangePicker from '../components/ExpenseDateRangePicker';
import InvoiceWizard from '../components/InvoiceWizard';
import { FilterableHeader, useTableFilters } from '../components/TableFilters';
import { exportToCSV, exportToXLSX, exportToPDF } from '../utils/exportTable';

const API = `${process.env.REACT_APP_BACKEND_URL || window.location.origin}/api`;

function FacturesBL() {
  const [currentUser] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('pharmacy_user') || localStorage.getItem('pharmacy_user')); } catch { return null; }
  });

  const { canCreate, canUpdate, canDelete, hasPermission } = useGranularPermissions();

  // Permissions factures_bl
  const perms = useMemo(() => {
    const p = currentUser?.granular_permissions?.factures_bl;
    const isAdminRole = ['super_admin', 'admin', 'gerant', 'comptable'].includes(currentUser?.role);
    return {
      view_history: hasPermission('factures_bl', 'view_history'),
      view_totals: hasPermission('factures_bl', 'view_totals'),
      view_all_users: hasPermission('factures_bl', 'view_all_users'),
      period_scope: p?.period_scope || (isAdminRole ? 'all' : 'month'),
    };
  }, [currentUser, hasPermission]);

  // Initial date range based on period_scope
  const initialRange = useMemo(() => {
    const today = new Date();
    switch (perms.period_scope) {
      case 'day': return { from: startOfDay(today), to: endOfDay(today) };
      case 'week': return { from: startOfWeek(today, { weekStartsOn: 6 }), to: endOfWeek(today, { weekStartsOn: 6 }) };
      case 'month': return { from: startOfMonth(today), to: endOfMonth(today) };
      case 'all':
      default: return { from: startOfMonth(today), to: endOfMonth(today) };
    }
  }, [perms.period_scope]);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('factures');
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [superAdminPharmacyFilter, setSuperAdminPharmacyFilter] = useState([]);
  const [dateRange, setDateRange] = useState(initialRange);
  const [wizardOpen, setWizardOpen] = useState(false);
  const [editInvoice, setEditInvoice] = useState(null);
  const [payModalOpen, setPayModalOpen] = useState(false);
  const [payTarget, setPayTarget] = useState(null);
  const [payForm, setPayForm] = useState({ amount: '', date: format(new Date(), 'yyyy-MM-dd'), payment_method: 'cash', notes: '' });

  // Overdue invoices modal
  const [overdueOpen, setOverdueOpen] = useState(false);
  const [overdueData, setOverdueData] = useState(null);
  const [overdueLoading, setOverdueLoading] = useState(false);
  const [overdueThisMonth, setOverdueThisMonth] = useState(true);

  // Export dropdown
  const [exportOpen, setExportOpen] = useState(false);
  const exportRef = React.useRef(null);
  useEffect(() => {
    if (!exportOpen) return;
    const onClick = (e) => { if (exportRef.current && !exportRef.current.contains(e.target)) setExportOpen(false); };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [exportOpen]);

  const headers = { 'X-User-Id': currentUser?.id || '' };

  useEffect(() => { fetchData(); }, [dateRange, superAdminPharmacyFilter]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const startDate = dateRange?.from || (dateRange?.from === null ? new Date(2000, 0, 1) : startOfMonth(new Date()));
      const endDate = dateRange?.to || (dateRange?.to === null ? new Date(2100, 11, 31) : endOfMonth(new Date()));
      let pharmacyIds = currentUser?.role === 'super_admin'
        ? (superAdminPharmacyFilter.length === 0 ? 'all' : superAdminPharmacyFilter.join(','))
        : 'all';

      const params = {
        start_date: new Date(startDate.getFullYear(), startDate.getMonth(), startDate.getDate(), 0, 0, 0).toISOString(),
        end_date: new Date(endDate.getFullYear(), endDate.getMonth(), endDate.getDate(), 23, 59, 59).toISOString(),
        pharmacy_ids: pharmacyIds,
        user_scope: perms.view_all_users ? 'all' : 'own',
        _t: new Date().getTime()
      };
      const res = await axios.get(`${API}/factures-bl-paiements`, { 
        headers: { ...headers, 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }, 
        params 
      });
      setData(res.data);
    } catch (err) {
      console.error(err);
      toast.error('Erreur chargement données');
    } finally {
      setLoading(false);
    }
  };

  const fmt = (v) => {
    if (v == null) return '0';
    return new Intl.NumberFormat('fr-DZ', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(v);
  };

  const fetchOverdue = async (thisMonth = true) => {
    setOverdueLoading(true);
    try {
      const params = { 
        user_scope: perms.view_all_users ? 'all' : 'own',
        _t: new Date().getTime()
      };
      if (thisMonth) params.this_month = true;
      if (currentUser?.role === 'super_admin' && superAdminPharmacyFilter.length > 0) {
        params.pharmacy_ids = superAdminPharmacyFilter.join(',');
      }
      const res = await axios.get(`${API}/supplier-invoices/overdue`, { 
        headers: { ...headers, 'Cache-Control': 'no-cache', 'Pragma': 'no-cache' }, 
        params 
      });
      setOverdueData(res.data);
    } catch (err) {
      console.error(err);
      toast.error('Erreur chargement factures en retard');
      setOverdueData({ count: 0, total_remaining: 0, items: [] });
    } finally {
      setOverdueLoading(false);
    }
  };

  const openOverdueModal = (thisMonth = true) => {
    setOverdueThisMonth(thisMonth);
    setOverdueOpen(true);
    fetchOverdue(thisMonth);
  };

  const getInvoiceStatus = (inv) => {
    // Single source of truth: trust backend's computed_status when present
    if (inv?.computed_status) return inv.computed_status;
    if (inv?.invoice_computed_status) return inv.invoice_computed_status;

    // Fallback: identical rules as backend's compute_invoice_display_status
    const total = inv?.amount || 0;
    const paid = inv?.paid_amount || 0;
    const remaining = total - paid;
    // 1. Paid takes precedence
    if (inv?.status === 'paid' || (paid >= total && total > 0)) return 'paid';
    // 2. Overdue: due_date < today AND remaining > 0
    if (inv?.due_date && remaining > 0) {
      const dueDate = new Date(inv.due_date);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      dueDate.setHours(0, 0, 0, 0);
      if (!isNaN(dueDate.getTime()) && dueDate < today) return 'overdue';
    }
    // 3. Partial / 4. Pending
    if (paid > 0) return 'partial';
    return 'pending';
  };

  const openPayModal = (inv) => {
    const remaining = Math.max((inv.amount || 0) - (inv.paid_amount || 0), 0);
    setPayTarget(inv);
    const defaultDate = inv.due_date ? new Date(inv.due_date) : (inv.date ? parseSafeDate(inv.date) : new Date());
    setPayForm({ amount: String(remaining), date: format(defaultDate, 'yyyy-MM-dd'), payment_method: 'cash', notes: '' });
    setPayModalOpen(true);
  };

  const submitPayment = async () => {
    if (!payTarget) return;
    try {
      await axios.post(`${API}/invoices/${payTarget.id}/pay`, {
        amount: parseFloat(payForm.amount) || 0,
        date: parseSafeDate(payForm.date).toISOString(),
        payment_method: payForm.payment_method,
        notes: payForm.notes,
      }, { headers });
      toast.success('Paiement enregistré !');
      setPayModalOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleDeleteInvoice = async (inv) => {
    if (!window.confirm(`Supprimer la facture ${inv.invoice_number || inv.id?.slice(0,8)} ?`)) return;
    try {
      await axios.delete(`${API}/suppliers/${inv.supplier_id}/invoices/${inv.id}`, { headers });
      toast.success('Facture supprimée');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur suppression'); }
  };

  const handleDeleteExpense = async (expense) => {
    if (!window.confirm(`Supprimer le BL ?`)) return;
    try {
      await axios.delete(`${API}/expenses/${expense.id}`, { headers });
      toast.success('BL supprimé');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur suppression'); }
  };

  const handleEditInvoice = (inv) => {
    setEditInvoice(inv);
    setWizardOpen(true);
  };

  const handleEditBL = (bl) => {
    // Try to locate the parent invoice (same supplier + invoice_number)
    let parentInvoice = null;
    if (bl.invoice_number) {
      parentInvoice = invoices.find(inv =>
        (inv.invoice_number || '').trim() === (bl.invoice_number || '').trim() &&
        (inv.supplier_name || '').trim() === (bl.supplier || '').trim()
      );
    }
    if (parentInvoice) {
      setEditInvoice(parentInvoice);
      setWizardOpen(true);
      return;
    }
    // No parent invoice → open wizard in create mode pre-filled with BL data
    const supplier = (data?.suppliers || []).find(s => s.name === bl.supplier);
    if (!supplier) {
      toast.error('Fournisseur introuvable pour ce BL. Créez-le d\'abord.');
      return;
    }
    setEditInvoice({
      // NO id → wizard treats this as pre-filled "new invoice"
      supplier_id: supplier.id,
      supplier_name: supplier.name,
      invoice_number: bl.invoice_number || '',
      bl_number: bl.id,
      date: bl.date,
      amount: bl.amount,
      amount_ht: bl.amount,
      tva_rate: 0,
      paid_amount: 0,
      payment_method: bl.payment_method || 'cash',
      notes: bl.description || '',
    });
    setWizardOpen(true);
  };

  const handleEditPayment = (pay) => {
    // Find parent invoice by invoice_id
    const parentInvoice = pay.invoice_id
      ? invoices.find(inv => inv.id === pay.invoice_id)
      : null;
    if (!parentInvoice) {
      toast.error('Ce paiement n\'est lié à aucune facture. Impossible d\'ouvrir la fiche.');
      return;
    }
    setEditInvoice(parentInvoice);
    setWizardOpen(true);
  };

  const handleDeletePayment = async (pay) => {
    if (!window.confirm('Supprimer ce paiement ?')) return;
    try {
      await axios.delete(`${API}/suppliers/${pay.supplier_id}/payments/${pay.id}`, { headers });
      toast.success('Paiement supprimé');
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Erreur'); }
  };

  const kpi = data?.kpi || {};
  const allInvoices = data?.invoices || [];
  const invoices = allInvoices.filter(inv => inv.document_type !== 'bl');
  const blsFromInvoices = allInvoices.filter(inv => inv.document_type === 'bl');
  const legacyBls = data?.bls || [];
  const bls = [...blsFromInvoices, ...legacyBls].sort((a, b) => new Date(b.date || b.created_at || 0) - new Date(a.date || a.created_at || 0));
  const payments = data?.payments || [];

  // Filter by search + status
  const filterItems = (items, fields, statusFn) => {
    let result = items;
    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      result = result.filter(item => fields.some(f => (item[f] || '').toLowerCase().includes(q)));
    }
    if (statusFilter !== 'all' && statusFn) {
      result = result.filter(item => statusFn(item) === statusFilter);
    }
    return result;
  };

  const getBLStatus = (bl) => {
    if (bl.document_type === 'bl') return getInvoiceStatus(bl);
    if (bl.invoice_computed_status) return bl.invoice_computed_status;
    if (bl.invoice_status) return bl.invoice_status;
    return 'pending';
  };

  const filteredInvoices = filterItems(invoices, ['invoice_number', 'bl_number', 'supplier_name', 'notes'], getInvoiceStatus);
  const filteredBLs = filterItems(bls, ['description', 'supplier', 'supplier_name', 'category_name', 'invoice_number', 'bl_number'], getBLStatus);
  const filteredPayments = filterItems(payments, ['reference', 'supplier_name', 'notes', 'invoice_number', 'bl_number'], (pay) => pay.invoice_computed_status || pay.invoice_status || 'paid');

  const [tableFilteredItems, setTableFilteredItems] = useState(null);
  const activeItemsRaw = activeTab === 'factures' ? filteredInvoices : activeTab === 'bls' ? filteredBLs : filteredPayments;

  const invoiceFilterConfig = [
    { key: 'supplier_name', accessor: (r) => r.supplier_name || r.supplier || '—' },
    { key: 'pharmacy_name', accessor: (r) => r.pharmacy_name || '—' },
    { key: 'status_label', accessor: (r) => {
      if (activeTab === 'factures') {
        const s = getInvoiceStatus(r);
        return s === 'paid' ? 'Payée' : s === 'partial' ? 'Partielle' : s === 'overdue' ? 'En retard' : 'Non payé';
      }
      if (activeTab === 'bls') {
        const hasInvoice = r.document_type === 'bl' ? true : !!r.invoice_number;
        if (!hasInvoice) return 'Non facturé';
        const s = r.document_type === 'bl' ? r.status : (r.invoice_status || 'pending');
        return s === 'paid' ? 'Payée' : s === 'partial' ? 'Partielle' : s === 'overdue' ? 'En retard' : 'Non payé';
      }
      const s = r.invoice_computed_status || r.invoice_status || 'paid';
      return s === 'paid' ? 'Payée' : s === 'partial' ? 'Partielle' : 'Non payé';
    }},
    { key: 'created_by', accessor: (r) => r.created_by_username || '—' },
  ];
  const { filters: invFilters, setFilter: setInvFilter, getUniqueValues: invUniqueValues, filteredData: invFilteredData } = useTableFilters(activeItemsRaw, invoiceFilterConfig);

  // Sorting state (default: date desc = recent to old)
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });

  const handleSort = (key) => {
    setSortConfig(current => {
      if (current.key === key) {
        return { key, direction: current.direction === 'desc' ? 'asc' : 'desc' };
      }
      return { key, direction: 'desc' };
    });
  };

  const activeItems = useMemo(() => {
    if (!invFilteredData || invFilteredData.length === 0) return invFilteredData;
    const sorted = [...invFilteredData];
    const { key, direction } = sortConfig;
    const mult = direction === 'asc' ? 1 : -1;

    sorted.sort((a, b) => {
      let valA = 0;
      let valB = 0;

      if (key === 'date') {
        const dateStrA = activeTab === 'factures' ? (a.due_date || a.date) :
                         activeTab === 'bls' ? (a.invoice_due_date || a.date) :
                         (a.date || a.invoice_due_date);
        const dateStrB = activeTab === 'factures' ? (b.due_date || b.date) :
                         activeTab === 'bls' ? (b.invoice_due_date || b.date) :
                         (b.date || b.invoice_due_date);
        valA = dateStrA ? new Date(dateStrA).getTime() : 0;
        valB = dateStrB ? new Date(dateStrB).getTime() : 0;
      } else if (key === 'amount') {
        valA = activeTab === 'factures' ? (a.amount || 0) :
               activeTab === 'bls' ? (a.document_type === 'bl' ? (a.amount || 0) : (a.invoice_amount != null ? a.invoice_amount : (a.amount || 0))) :
               (a.invoice_amount != null ? a.invoice_amount : (a.amount || 0));
        valB = activeTab === 'factures' ? (b.amount || 0) :
               activeTab === 'bls' ? (b.document_type === 'bl' ? (b.amount || 0) : (b.invoice_amount != null ? b.invoice_amount : (b.amount || 0))) :
               (b.invoice_amount != null ? b.invoice_amount : (b.amount || 0));
      } else if (key === 'paid') {
        valA = activeTab === 'factures' ? (a.paid_amount || 0) :
               activeTab === 'bls' ? (a.document_type === 'bl' ? (a.paid_amount || 0) : (a.invoice_paid_amount != null ? a.invoice_paid_amount : 0)) :
               (a.amount || 0);
        valB = activeTab === 'factures' ? (b.paid_amount || 0) :
               activeTab === 'bls' ? (b.document_type === 'bl' ? (b.paid_amount || 0) : (b.invoice_paid_amount != null ? b.invoice_paid_amount : 0)) :
               (b.amount || 0);
      } else if (key === 'remaining') {
        valA = activeTab === 'factures' ? Math.max((a.amount || 0) - (a.paid_amount || 0), 0) :
               activeTab === 'bls' ? (a.document_type === 'bl' ? Math.max((a.amount || 0) - (a.paid_amount || 0), 0) : (a.invoice_remaining != null ? a.invoice_remaining : (a.amount || 0))) :
               (a.invoice_remaining != null ? a.invoice_remaining : 0);
        valB = activeTab === 'factures' ? Math.max((b.amount || 0) - (b.paid_amount || 0), 0) :
               activeTab === 'bls' ? (b.document_type === 'bl' ? Math.max((b.amount || 0) - (b.paid_amount || 0), 0) : (b.invoice_remaining != null ? b.invoice_remaining : (b.amount || 0))) :
               (b.invoice_remaining != null ? b.invoice_remaining : 0);
      }

      if (valA < valB) return -1 * mult;
      if (valA > valB) return 1 * mult;
      return 0;
    });

    return sorted;
  }, [invFilteredData, sortConfig, activeTab]);

  const SortableHeader = ({ label, sortKey, className = "" }) => {
    const isSorted = sortConfig.key === sortKey;
    return (
      <th
        onClick={() => handleSort(sortKey)}
        className={`text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase cursor-pointer select-none hover:text-gray-900 transition-colors ${className}`}
      >
        <div className="inline-flex items-center gap-1">
          <span>{label}</span>
          {isSorted ? (
            sortConfig.direction === 'desc' ? (
              <ArrowDown className="w-3.5 h-3.5 text-blue-600 font-bold" />
            ) : (
              <ArrowUp className="w-3.5 h-3.5 text-blue-600 font-bold" />
            )
          ) : (
            <ArrowUpDown className="w-3 h-3 text-gray-300 hover:text-gray-500" />
          )}
        </div>
      </th>
    );
  };

  // Reset table filters when switching tabs
  useEffect(() => { setTableFilteredItems(null); }, [activeTab]);

  // Build export columns per active tab
  const buildExportConfig = () => {
    const fmtDate = (d) => d ? format(new Date(d), 'dd/MM/yyyy', { locale: fr }) : '';
    const statusLabel = (r) => {
      const s = getInvoiceStatus(r);
      return s === 'paid' ? 'Payée' : s === 'partial' ? 'Partielle' : s === 'overdue' ? 'En retard' : 'En attente';
    };
    const todayStamp = format(new Date(), 'yyyy-MM-dd');
    if (activeTab === 'factures') {
      return {
        filename: `factures_${todayStamp}`,
        title: `Factures — exporté le ${format(new Date(), 'dd MMM yyyy', { locale: fr })} (${activeItems.length} ligne${activeItems.length > 1 ? 's' : ''})`,
        columns: [
          { label: 'N° Facture/BL', value: (r) => r.invoice_number || r.bl_number || r.id?.slice(0, 8) || '', width: 18 },
          { label: 'Pharmacie', value: (r) => r.pharmacy_name || '', width: 18 },
          { label: 'Utilisateur', value: (r) => r.created_by_username || '', width: 18 },
          { label: 'Type', value: (r) => r.parent_category || '', width: 14 },
          { label: 'Fournisseur', value: (r) => r.supplier_name || '', width: 22 },
          { label: 'Montant TTC', value: (r) => r.amount || 0, width: 14 },
          { label: 'Payé', value: (r) => r.paid_amount || 0, width: 14 },
          { label: 'Reste', value: (r) => Math.max((r.amount || 0) - (r.paid_amount || 0), 0), width: 14 },
          { label: 'Date facture', value: (r) => fmtDate(r.date), width: 14 },
          { label: 'Échéance', value: (r) => fmtDate(r.due_date), width: 14 },
          { label: 'Statut', value: statusLabel, width: 14 },
          { label: 'Notes', value: (r) => r.notes || '', width: 26 },
        ],
      };
    }
    if (activeTab === 'bls') {
      return {
        filename: `bons_de_livraison_${todayStamp}`,
        title: `Bons de livraison — exporté le ${format(new Date(), 'dd MMM yyyy', { locale: fr })} (${activeItems.length} ligne${activeItems.length > 1 ? 's' : ''})`,
        columns: [
          { label: 'N° BL', value: (r) => r.bl_number || r.invoice_number || r.id?.slice(0, 8) || '', width: 14 },
          { label: 'Pharmacie', value: (r) => r.pharmacy_name || '', width: 18 },
          { label: 'Utilisateur', value: (r) => r.created_by_username || '', width: 18 },
          { label: 'Type', value: (r) => r.parent_category || '', width: 14 },
          { label: 'Fournisseur', value: (r) => r.supplier || '', width: 22 },
          { label: 'Montant', value: (r) => r.amount || 0, width: 14 },
          { label: 'Payé', value: (r) => r.invoice_paid_amount || 0, width: 14 },
          { label: 'Reste', value: (r) => r.invoice_remaining != null ? r.invoice_remaining : (r.amount || 0), width: 14 },
          { label: 'Date BL', value: (r) => fmtDate(r.date), width: 14 },
          { label: 'Échéance', value: (r) => fmtDate(r.invoice_due_date), width: 14 },
          { label: 'Statut', value: (r) => {
            const s = r.invoice_computed_status || r.invoice_status || (r.invoice_number ? 'pending' : 'pending');
            return s === 'paid' ? 'Payée' : s === 'partial' ? 'Partielle' : s === 'overdue' ? 'En retard' : (r.invoice_number ? 'En attente' : 'Non facturé');
          }, width: 14 },
        ],
      };
    }
    // payments
    return {
      filename: `paiements_${todayStamp}`,
      title: `Paiements — exporté le ${format(new Date(), 'dd MMM yyyy', { locale: fr })} (${activeItems.length} ligne${activeItems.length > 1 ? 's' : ''})`,
      columns: [
        { label: 'N° Paiement', value: (r) => r.id?.slice(0, 8) || '', width: 14 },
        { label: 'Pharmacie', value: (r) => r.pharmacy_name || '', width: 18 },
        { label: 'Utilisateur', value: (r) => r.created_by_username || '', width: 18 },
        { label: 'Fournisseur', value: (r) => r.supplier_name || '', width: 22 },
        { label: 'N° Facture/BL', value: (r) => r.invoice_number || r.bl_number || '', width: 18 },
        { label: 'Montant payé', value: (r) => r.amount || 0, width: 14 },
        { label: 'Mode', value: (r) => r.payment_method || '', width: 12 },
        { label: 'Date paiement', value: (r) => fmtDate(r.date), width: 14 },
        { label: 'Notes', value: (r) => r.notes || '', width: 26 },
      ],
    };
  };

  const handleExport = (formatType) => {
    if (!activeItems || activeItems.length === 0) {
      toast.error('Aucune donnée à exporter');
      setExportOpen(false);
      return;
    }
    const cfg = buildExportConfig();
    try {
      if (formatType === 'csv')   exportToCSV(activeItems,  cfg.columns, cfg.filename);
      if (formatType === 'xlsx')  exportToXLSX(activeItems, cfg.columns, cfg.filename);
      if (formatType === 'pdf')   exportToPDF(activeItems,  cfg.columns, cfg.filename, cfg.title);
      toast.success(`Export ${formatType.toUpperCase()} (${activeItems.length} lignes)`);
    } catch (err) {
      console.error(err);
      toast.error("Erreur lors de l'export");
    }
    setExportOpen(false);
  };

  return (
    <div className="space-y-4 sm:space-y-6 min-h-screen p-3 sm:p-6 bg-gray-50 fade-in">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Factures, BL & Paiements</h1>
          <p className="text-gray-500 mt-1 text-sm sm:text-base">Suivi financier fournisseurs</p>
        </div>

      </div>

      {/* Filtres */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex-1 min-w-0">
            {currentUser?.role === 'super_admin' && (
              <ExpensePharmacyFilter selectedPharmacies={superAdminPharmacyFilter} onChange={setSuperAdminPharmacyFilter} currentUser={currentUser} />
            )}
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {perms.period_scope === 'all' ? (
              <ExpenseDateRangePicker dateRange={dateRange} onDateRangeChange={setDateRange} />
            ) : (
              <div className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 bg-gray-50">
                <Clock className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-600">Période verrouillée :</span>
                <span className="text-sm font-semibold text-gray-900">
                  {perms.period_scope === 'day' && format(dateRange.from, 'dd MMM yyyy', { locale: fr })}
                  {perms.period_scope === 'week' && `Semaine du ${format(dateRange.from, 'dd MMM', { locale: fr })} au ${format(dateRange.to, 'dd MMM yyyy', { locale: fr })}`}
                  {perms.period_scope === 'month' && format(dateRange.from, 'MMMM yyyy', { locale: fr })}
                </span>
              </div>
            )}
            {canCreate('factures_bl') && (
              <button
                onClick={() => setWizardOpen(true)}
                data-testid="add-invoice-top-btn"
                className="flex items-center gap-2 px-8 py-3 rounded-xl text-sm font-semibold transition-all border-2 bg-white text-blue-600 border-blue-200 hover:bg-blue-50 hover:border-blue-300 shadow-sm"
              >
                <Plus className="w-5 h-5" />
                Nouvelle facture
              </button>
            )}
          </div>
        </div>
        {!perms.view_all_users && (
          <div className="mt-3 flex items-center gap-2 text-xs text-gray-500">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
            Vous consultez uniquement vos propres entrées
          </div>
        )}
      </div>

      {/* 4 KPI Cards */}
      {perms.view_totals && (
        <div className="grid grid-cols-[repeat(auto-fit,minmax(240px,1fr))] gap-4 mb-6">
          {/* Total Facturé */}
          <Card className="stat-card border-l-4 border-l-emerald-500 hover:-translate-y-1 hover:shadow-lg transition-all duration-300 cursor-pointer">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-emerald-50 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-emerald-600" />
                </div>
                <CardTitle className="text-sm font-medium text-gray-600">Total facturé</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-emerald-600">{fmt(kpi.total_invoiced)} DA</div>
              <div className="flex items-center gap-1 text-xs mt-1">
                <ArrowUpRight className="w-3.5 h-3.5 text-emerald-600" />
                <span className="font-semibold text-emerald-600">+12%</span>
                <span className="text-gray-500">vs mois dernier</span>
              </div>
              <p className="text-xs text-gray-400 mt-2">{kpi.invoice_count || 0} factures</p>
              {/* Mini graphique */}
              <div className="mt-2 h-8">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={[{ val: 400 }, { val: 500 }, { val: 450 }, { val: 600 }, { val: 550 }, { val: 700 }, { val: 800 }]}>
                    <Line type="monotone" dataKey="val" stroke="#10b981" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Bons de Livraison */}
          <Card className="stat-card border-l-4 border-l-blue-500 hover:-translate-y-1 hover:shadow-lg transition-all duration-300 cursor-pointer">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-blue-50 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-blue-600" />
                </div>
                <CardTitle className="text-sm font-medium text-gray-600">Bons de livraison</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-blue-600">{kpi.bl_count || 0} <span className="text-sm font-normal text-gray-500">réceptions</span></div>
              <div className="flex items-center gap-1 text-xs mt-1">
                <ArrowUpRight className="w-3.5 h-3.5 text-blue-600" />
                <span className="font-semibold text-blue-600">BLs reçus</span>
              </div>
              <p className="text-xs text-gray-400 mt-2">{kpi.bl_pending || 0} en attente</p>
              {/* Mini graphique */}
              <div className="mt-2 h-8">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={[{ val: 200 }, { val: 250 }, { val: 300 }, { val: 350 }, { val: 400 }, { val: 450 }, { val: 500 }]}>
                    <Line type="monotone" dataKey="val" stroke="#3b82f6" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Dette Totale */}
          <Card className="stat-card border-l-4 border-l-red-500 hover:-translate-y-1 hover:shadow-lg transition-all duration-300 cursor-pointer">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-red-50 flex items-center justify-center">
                  <TrendingDown className="w-5 h-5 text-red-600" />
                </div>
                <CardTitle className="text-sm font-medium text-gray-600">Dette totale</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-red-600">{fmt(kpi.debt)} DA</div>
              <div className="flex items-center gap-1 text-xs mt-1">
                <ArrowUpRight className="w-3.5 h-3.5 text-red-600" />
                <span className="font-semibold text-red-600">+{fmt(Math.abs(kpi.debt || 0))}</span>
                <span className="text-gray-500">vs mois précédent</span>
              </div>
              <p className="text-xs text-gray-400 mt-2">Dont {kpi.overdue_count || 0} en retard</p>
              {/* Mini graphique */}
              <div className="mt-2 h-8">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={[{ val: 500 }, { val: 450 }, { val: 400 }, { val: 350 }, { val: 300 }, { val: 290 }, { val: 285 }]}>
                    <Line type="monotone" dataKey="val" stroke="#ef4444" strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Taux Règlement */}
          <Card className="stat-card border-l-4 border-l-orange-500 hover:-translate-y-1 hover:shadow-lg transition-all duration-300 cursor-pointer">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-orange-50 flex items-center justify-center">
                  <Check className="w-5 h-5 text-orange-600" />
                </div>
                <CardTitle className="text-sm font-medium text-gray-600">Taux règlement</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold mb-1" style={{ color: (kpi.rate || 0) >= 70 ? '#10b981' : '#f97316' }}>
                {kpi.rate || 0} %
              </div>
              <div className="flex items-center gap-1 text-xs mt-1">
                <span className="font-semibold" style={{ color: (kpi.rate || 0) >= 70 ? '#10b981' : '#f97316' }}>
                  {Math.min(kpi.rate || 0, 100)}%
                </span>
                <span className="text-gray-500">de règlements réalisés</span>
              </div>
              <p className="text-xs text-gray-400 mt-2">Objectif : 90%</p>
              {/* Mini graphique */}
              <div className="mt-2 h-8">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={[{ val: 250 }, { val: 350 }, { val: 450 }, { val: 500 }, { val: 600 }, { val: 700 }, { val: 730 }]}>
                    <Line type="monotone" dataKey="val" stroke={(kpi.rate || 0) >= 70 ? "#10b981" : "#f97316"} strokeWidth={2} dot={false} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Transactions Section */}
      <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
        {/* Section Header: Transactions title + Tabs + Filtrer/Exporter */}
        <div className="p-4 sm:p-5 pb-0">
          <div className="flex flex-col gap-3">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div className="flex items-center gap-4">
                <h2 className="text-lg font-bold text-gray-900">Transactions</h2>
              </div>
              <div className="flex gap-2 flex-wrap">
                <div className="relative" ref={exportRef}>
                  <Button
                    variant="outline"
                    onClick={() => setExportOpen(o => !o)}
                    data-testid="export-btn"
                    className="rounded-lg border-gray-200 text-gray-600 text-sm h-9"
                  >
                    <Download className="w-4 h-4 mr-1.5" /> Exporter
                    <ChevronDown className={`w-3.5 h-3.5 ml-1 transition-transform ${exportOpen ? 'rotate-180' : ''}`} />
                  </Button>
                  {exportOpen && (
                    <div className="absolute right-0 top-full mt-1 z-50 bg-white border border-gray-200 rounded-xl shadow-xl min-w-[180px] overflow-hidden" data-testid="export-menu">
                      <button onClick={() => handleExport('csv')} data-testid="export-csv" className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center justify-between">
                        <span>Exporter en CSV</span>
                        <span className="text-xs text-gray-400 font-mono">.csv</span>
                      </button>
                      <button onClick={() => handleExport('xlsx')} data-testid="export-xlsx" className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center justify-between border-t border-gray-100">
                        <span>Exporter en Excel</span>
                        <span className="text-xs text-gray-400 font-mono">.xlsx</span>
                      </button>
                      <button onClick={() => handleExport('pdf')} data-testid="export-pdf" className="w-full text-left px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 flex items-center justify-between border-t border-gray-100">
                        <span>Exporter en PDF</span>
                        <span className="text-xs text-gray-400 font-mono">.pdf</span>
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-1 pb-1">
              <button
                onClick={() => { setActiveTab('factures'); setStatusFilter('all'); }}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5 whitespace-nowrap ${activeTab === 'factures' ? 'bg-white text-gray-900 border border-gray-200 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
              >
                Factures
                <span className={`px-1.5 py-0.5 rounded text-xs font-bold ${activeTab === 'factures' ? 'bg-gray-900 text-white' : 'bg-gray-200 text-gray-600'}`}>{invoices.length}</span>
              </button>
              <button
                onClick={() => { setActiveTab('bls'); setStatusFilter('all'); }}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5 whitespace-nowrap ${activeTab === 'bls' ? 'bg-white text-gray-900 border border-gray-200 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
              >
                BL
                <span className={`px-1.5 py-0.5 rounded text-xs font-bold ${activeTab === 'bls' ? 'bg-gray-900 text-white' : 'bg-gray-200 text-gray-600'}`}>{bls.length}</span>
              </button>
              <button
                onClick={() => { setActiveTab('paiements'); setStatusFilter('all'); }}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all flex items-center gap-1.5 whitespace-nowrap ${activeTab === 'paiements' ? 'bg-white text-gray-900 border border-gray-200 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
              >
                Paiements
                <span className={`px-1.5 py-0.5 rounded text-xs font-bold ${activeTab === 'paiements' ? 'bg-gray-900 text-white' : 'bg-gray-200 text-gray-600'}`}>{payments.length}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Search + Status Filter Pills */}
        <div className="px-4 sm:px-5 py-3">
          <div className="flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center gap-2 bg-gray-50 rounded-xl p-2">
            <div className="relative w-full sm:w-auto">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Rechercher facture, fournisseur..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="pl-9 py-2 rounded-lg border-0 bg-white shadow-sm w-full sm:w-56 text-sm h-9"
              />
            </div>
            {activeTab !== 'paiements' && (
              <div className="flex gap-2 flex-wrap">
              {['all', 'pending', 'partial', 'overdue', 'paid'].map(s => (
                <button
                  key={s}
                  onClick={() => setStatusFilter(s)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-all ${statusFilter === s
                    ? 'bg-gray-900 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'}`}
                >
                  {s === 'all' ? 'Tous' : s === 'pending' ? 'Non payé' : s === 'partial' ? 'Partielles' : s === 'overdue' ? 'En retard' : 'Payées'}
                </button>
              ))}
              </div>
            )}
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          {!perms.view_history ? (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-center m-4">
              <p className="text-sm text-yellow-700">Vous n'avez pas la permission de voir l'historique des factures / BL</p>
            </div>
          ) : loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500" />
            </div>
          ) : activeTab === 'factures' ? (
            <table className="w-full">
              <thead>
                <tr className="border-t border-gray-100">
                  <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase">N° Facture/BL</th>
                  <FilterableHeader label="Pharmacie" allLabel="TOUS" values={invUniqueValues['pharmacy_name'] || []} selected={invFilters.pharmacy_name} onSelect={(v) => setInvFilter('pharmacy_name', v)} className="hidden lg:table-cell" />
                  <FilterableHeader label="Utilisateur" allLabel="TOUS" values={invUniqueValues['created_by'] || []} selected={invFilters.created_by} onSelect={(v) => setInvFilter('created_by', v)} className="hidden xl:table-cell" />
                  <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase hidden md:table-cell">Type</th>
                  <FilterableHeader label="Fournisseur" allLabel="TOUS" values={invUniqueValues['supplier_name'] || []} selected={invFilters.supplier_name} onSelect={(v) => setInvFilter('supplier_name', v)} />
                  <SortableHeader label="Montant TTC" sortKey="amount" />
                  <SortableHeader label="Payé" sortKey="paid" className="hidden md:table-cell" />
                  <SortableHeader label="Reste" sortKey="remaining" className="hidden md:table-cell" />
                  <SortableHeader label="Échéance" sortKey="date" className="hidden sm:table-cell" />
                  <FilterableHeader label="Statut" allLabel="TOUS" values={invUniqueValues['status_label'] || []} selected={invFilters.status_label} onSelect={(v) => setInvFilter('status_label', v)} />
                  <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase">Action</th>
                </tr>
              </thead>
              <tbody>
                {activeItems.length === 0 ? (
                  <tr><td colSpan={11} className="text-center py-12 text-gray-400">Aucune facture</td></tr>
                ) : activeItems.map((inv, idx) => {
                  const status = getInvoiceStatus(inv);
                  const originalIndex = invoices.findIndex(i => i.id === inv.id);
                  const stableIdx = originalIndex >= 0 ? invoices.length - originalIndex : (idx + 1);
                  const ref = inv.invoice_number || inv.bl_number || `FAC-${String(stableIdx).padStart(4, '0')}`;
                  const paid = inv.paid_amount || 0;
                  const remaining = Math.max((inv.amount || 0) - paid, 0);
                  return (
                    <tr key={inv.id || idx} className="border-t border-gray-50 hover:bg-gray-50/50">
                      <td className="px-4 py-4 text-sm font-semibold text-gray-900">{ref}</td>
                      <td className="px-4 py-4 hidden lg:table-cell">
                        {inv.pharmacy_name ? (
                          <span className="px-2 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700">{inv.pharmacy_name}</span>
                        ) : <span className="text-gray-400 text-sm">-</span>}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-700 hidden xl:table-cell">{inv.created_by_username || '-'}</td>
                      <td className="px-4 py-4 hidden md:table-cell">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                          (inv.parent_category || '').includes('Achat') ? 'bg-blue-100 text-blue-700' :
                          (inv.parent_category || '').includes('Charge') ? 'bg-red-100 text-red-700' :
                          (inv.parent_category || '').includes('Salaire') ? 'bg-orange-100 text-orange-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>{inv.parent_category || '-'}</span>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-700">{inv.supplier_name || '-'}</td>
                      <td className="px-4 py-4 text-sm font-bold text-gray-900 whitespace-nowrap">{fmt(inv.amount)} DA</td>
                      <td className="px-4 py-4 text-sm font-semibold hidden md:table-cell" style={{ color: paid > 0 ? '#2E7D32' : '#999' }}>{fmt(paid)} DA</td>
                      <td className="px-4 py-4 text-sm font-semibold hidden md:table-cell" style={{ color: remaining > 0 ? '#C62828' : '#2E7D32' }}>{fmt(remaining)} DA</td>
                      <td className={`px-4 py-4 text-sm hidden sm:table-cell ${status === 'overdue' ? 'text-red-600 font-semibold' : 'text-gray-600'}`}>
                        {inv.due_date ? format(new Date(inv.due_date), 'dd MMM yyyy', { locale: fr }) : inv.date ? format(parseSafeDate(inv.date), 'dd MMM yyyy', { locale: fr }) : '-'}
                      </td>
                      <td className="px-4 py-4">
                        {status === 'paid' ? (
                          <span className="inline-flex items-center gap-1.5 text-xs font-semibold" style={{ color: '#2E7D32' }}><span className="w-2 h-2 rounded-full" style={{ backgroundColor: '#4CAF50' }} /> Payée</span>
                        ) : status === 'partial' ? (
                          <span className="inline-flex items-center gap-1.5 text-xs font-semibold" style={{ color: '#E65100' }}><span className="w-2 h-2 rounded-full" style={{ backgroundColor: '#FF9800' }} /> Partielle</span>
                        ) : status === 'overdue' ? (
                          <span className="inline-flex items-center gap-1.5 text-xs font-semibold" style={{ color: '#C62828' }}><span className="w-2 h-2 rounded-full" style={{ backgroundColor: '#E53935' }} /> En retard</span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 text-xs font-semibold" style={{ color: '#E65100' }}><span className="w-2 h-2 rounded-full" style={{ backgroundColor: '#FF9800' }} /> Non payé</span>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-1">
                          {canUpdate('factures_bl') && (
                            <button onClick={() => handleEditInvoice(inv)} className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-gray-50" title="Modifier">
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                          )}
                          {canDelete('factures_bl') && (
                            <button onClick={() => handleDeleteInvoice(inv)} className="w-8 h-8 rounded-lg border border-red-200 flex items-center justify-center text-red-400 hover:bg-red-50" title="Supprimer">
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                          {status === 'paid' ? (
                            <span className="px-2 py-1 rounded text-xs font-semibold" style={{ backgroundColor: '#E8F5E9', color: '#2E7D32' }}>Payé</span>
                          ) : status === 'partial' ? (
                            <button onClick={() => openPayModal(inv)} className="px-2 py-1 rounded text-xs font-semibold" style={{ backgroundColor: '#FFF3E0', color: '#E65100' }}>Partiel</button>
                          ) : status === 'overdue' ? (
                            <button onClick={() => openPayModal(inv)} className="px-2 py-1 rounded text-xs font-semibold" style={{ backgroundColor: '#FFEBEE', color: '#C62828' }}>Non payé</button>
                          ) : (
                            <button onClick={() => openPayModal(inv)} className="px-2 py-1 rounded text-xs font-semibold" style={{ backgroundColor: '#FFF3E0', color: '#E65100' }}>Non payé</button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : activeTab === 'bls' ? (
            <table className="w-full">
              <thead>
                <tr className="border-t border-gray-100">
                  <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase">N° Facture/BL</th>
                  <FilterableHeader label="Pharmacie" allLabel="TOUS" values={invUniqueValues['pharmacy_name'] || []} selected={invFilters.pharmacy_name} onSelect={(v) => setInvFilter('pharmacy_name', v)} className="hidden lg:table-cell" />
                  <FilterableHeader label="Utilisateur" allLabel="TOUS" values={invUniqueValues['created_by'] || []} selected={invFilters.created_by} onSelect={(v) => setInvFilter('created_by', v)} className="hidden xl:table-cell" />
                  <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase hidden md:table-cell">Type</th>
                  <FilterableHeader label="Fournisseur" allLabel="TOUS" values={invUniqueValues['supplier_name'] || []} selected={invFilters.supplier_name} onSelect={(v) => setInvFilter('supplier_name', v)} />
                  <SortableHeader label="Montant TTC" sortKey="amount" />
                  <SortableHeader label="Payé" sortKey="paid" className="hidden md:table-cell" />
                  <SortableHeader label="Reste" sortKey="remaining" className="hidden md:table-cell" />
                  <SortableHeader label="Échéance" sortKey="date" className="hidden sm:table-cell" />
                  <FilterableHeader label="Statut" allLabel="TOUS" values={invUniqueValues['status_label'] || []} selected={invFilters.status_label} onSelect={(v) => setInvFilter('status_label', v)} />
                  <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase">Action</th>
                </tr>
              </thead>
              <tbody>
                {activeItems.length === 0 ? (
                  <tr><td colSpan={11} className="text-center py-12 text-gray-400">Aucun bon de livraison</td></tr>
                ) : activeItems.map((bl, idx) => {
                  const originalIndex = bls.findIndex(b => b.id === bl.id);
                  const stableIdx = originalIndex >= 0 ? bls.length - originalIndex : (idx + 1);
                  const invTotal = bl.document_type === 'bl' ? bl.amount : (bl.invoice_amount != null ? bl.invoice_amount : (bl.amount || 0));
                  const invPaid = bl.document_type === 'bl' ? (bl.paid_amount || 0) : (bl.invoice_paid_amount != null ? bl.invoice_paid_amount : 0);
                  const hasInvoice = bl.document_type === 'bl' ? true : !!bl.invoice_number;
                  const invRemaining = hasInvoice ? Math.max(invTotal - invPaid, 0) : bl.amount;
                  const invStatus = bl.document_type === 'bl' ? bl.status : (bl.invoice_status || (hasInvoice ? 'pending' : 'pending'));
                  return (
                    <tr key={bl.id || idx} className="border-t border-gray-50 hover:bg-gray-50/50">
                      <td className="px-4 py-4">
                        <span className="px-2.5 py-1 rounded text-xs font-bold" style={{ backgroundColor: '#E8F5E9', color: '#2E7D32' }}>{bl.bl_number || bl.invoice_number || `BL-${String(stableIdx).padStart(3, '0')}`}</span>
                      </td>
                      <td className="px-4 py-4 hidden lg:table-cell">
                        {bl.pharmacy_name ? (
                          <span className="px-2 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700">{bl.pharmacy_name}</span>
                        ) : <span className="text-gray-400 text-sm">-</span>}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-700 hidden xl:table-cell">{bl.created_by_username || '-'}</td>
                      <td className="px-4 py-4 hidden md:table-cell">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                          (bl.parent_category || '').includes('Achat') ? 'bg-blue-100 text-blue-700' :
                          (bl.parent_category || '').includes('Charge') ? 'bg-red-100 text-red-700' :
                          (bl.parent_category || '').includes('Salaire') ? 'bg-orange-100 text-orange-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>{bl.parent_category || '-'}</span>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-700">{bl.supplier || bl.supplier_name || '-'}</td>
                      <td className="px-4 py-4 text-sm font-bold text-gray-900 whitespace-nowrap">{fmt(bl.amount)} DA</td>
                      <td className="px-4 py-4 text-sm font-semibold hidden md:table-cell" style={{ color: invPaid > 0 ? '#2E7D32' : '#999' }}>{fmt(invPaid)} DA</td>
                      <td className="px-4 py-4 text-sm font-semibold hidden md:table-cell" style={{ color: invRemaining > 0 ? '#C62828' : '#2E7D32' }}>{fmt(invRemaining)} DA</td>
                      <td className="px-4 py-4 text-sm text-gray-600 hidden sm:table-cell">{bl.invoice_due_date ? format(new Date(bl.invoice_due_date), 'dd MMM yyyy', { locale: fr }) : bl.date ? format(parseSafeDate(bl.date), 'dd MMM yyyy', { locale: fr }) : '-'}</td>
                      <td className="px-4 py-4">
                        {!hasInvoice ? (
                          <span className="inline-flex items-center gap-1.5 text-xs font-semibold" style={{ color: '#E65100' }}><span className="w-2 h-2 rounded-full" style={{ backgroundColor: '#FF9800' }} /> Non facturé</span>
                        ) : invStatus === 'paid' ? (
                          <span className="inline-flex items-center gap-1.5 text-xs font-semibold" style={{ color: '#2E7D32' }}><span className="w-2 h-2 rounded-full" style={{ backgroundColor: '#4CAF50' }} /> Payée</span>
                        ) : invStatus === 'partial' ? (
                          <span className="inline-flex items-center gap-1.5 text-xs font-semibold" style={{ color: '#E65100' }}><span className="w-2 h-2 rounded-full" style={{ backgroundColor: '#FF9800' }} /> Partielle</span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 text-xs font-semibold" style={{ color: '#E65100' }}><span className="w-2 h-2 rounded-full" style={{ backgroundColor: '#FF9800' }} /> Non payé</span>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-1">
                          {canUpdate('factures_bl') && (
                            <button onClick={() => bl.document_type === 'bl' ? handleEditInvoice(bl) : handleEditBL(bl)} className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-gray-50" title="Modifier"><Pencil className="w-3.5 h-3.5" /></button>
                          )}
                          {canDelete('factures_bl') && (
                            <button onClick={() => bl.document_type === 'bl' ? handleDeleteInvoice(bl) : handleDeleteExpense(bl)} className="w-8 h-8 rounded-lg border border-red-200 flex items-center justify-center text-red-400 hover:bg-red-50" title="Supprimer"><Trash2 className="w-3.5 h-3.5" /></button>
                          )}
                          {invStatus === 'paid' ? (
                            <span className="px-2 py-1 rounded text-xs font-semibold" style={{ backgroundColor: '#E8F5E9', color: '#2E7D32' }}>Payé</span>
                          ) : invStatus === 'partial' ? (
                            bl.document_type === 'bl' ? <button onClick={() => openPayModal(bl)} className="px-2 py-1 rounded text-xs font-semibold" style={{ backgroundColor: '#FFF3E0', color: '#E65100' }}>Partiel</button> :
                            <span className="px-2 py-1 rounded text-xs font-semibold" style={{ backgroundColor: '#FFF3E0', color: '#E65100' }}>Partiel</span>
                          ) : (
                            bl.document_type === 'bl' ? <button onClick={() => openPayModal(bl)} className="px-2 py-1 rounded text-xs font-semibold" style={{ backgroundColor: '#FFF3E0', color: '#E65100' }}>Non payé</button> :
                            <span className="px-2 py-1 rounded text-xs font-semibold" style={{ backgroundColor: '#FFF3E0', color: '#E65100' }}>Non payé</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-t border-gray-100">
                  <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase">N° Facture/BL</th>
                  <FilterableHeader label="Pharmacie" allLabel="TOUS" values={invUniqueValues['pharmacy_name'] || []} selected={invFilters.pharmacy_name} onSelect={(v) => setInvFilter('pharmacy_name', v)} className="hidden lg:table-cell" />
                  <FilterableHeader label="Utilisateur" allLabel="TOUS" values={invUniqueValues['created_by'] || []} selected={invFilters.created_by} onSelect={(v) => setInvFilter('created_by', v)} className="hidden xl:table-cell" />
                  <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase hidden md:table-cell">Type</th>
                  <FilterableHeader label="Fournisseur" allLabel="TOUS" values={invUniqueValues['supplier_name'] || []} selected={invFilters.supplier_name} onSelect={(v) => setInvFilter('supplier_name', v)} />
                  <SortableHeader label="Montant TTC" sortKey="amount" />
                  <SortableHeader label="Payé" sortKey="paid" className="hidden md:table-cell" />
                  <SortableHeader label="Reste" sortKey="remaining" className="hidden md:table-cell" />
                  <SortableHeader label="Échéance" sortKey="date" className="hidden sm:table-cell" />
                  <FilterableHeader label="Statut" allLabel="TOUS" values={invUniqueValues['status_label'] || []} selected={invFilters.status_label} onSelect={(v) => setInvFilter('status_label', v)} />
                  <th className="text-left px-4 py-3 text-xs font-bold text-gray-500 uppercase">Action</th>
                </tr>
              </thead>
              <tbody>
                {activeItems.length === 0 ? (
                  <tr><td colSpan={11} className="text-center py-12 text-gray-400">Aucun paiement</td></tr>
                ) : activeItems.map((pay, idx) => {
                  const originalIndex = payments.findIndex(p => p.id === pay.id);
                  const stableIdx = originalIndex >= 0 ? payments.length - originalIndex : (idx + 1);
                  const invTotal = pay.invoice_amount != null ? pay.invoice_amount : (pay.amount || 0);
                  const payAmount = pay.amount || 0;
                  const invRemaining = pay.invoice_remaining != null ? pay.invoice_remaining : 0;
                  const invStatus = pay.invoice_computed_status || pay.invoice_status || 'paid';
                  return (
                    <tr key={pay.id || idx} className="border-t border-gray-50 hover:bg-gray-50/50">
                      <td className="px-4 py-4 text-sm font-semibold text-gray-900">{pay.invoice_number || pay.bl_number || pay.reference || `P-${String(stableIdx).padStart(3, '0')}`}</td>
                      <td className="px-4 py-4 hidden lg:table-cell">
                        {pay.pharmacy_name ? (
                          <span className="px-2 py-1 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700">{pay.pharmacy_name}</span>
                        ) : <span className="text-gray-400 text-sm">-</span>}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-700 hidden xl:table-cell">{pay.created_by_username || '-'}</td>
                      <td className="px-4 py-4 hidden md:table-cell">
                        <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                          (pay.parent_category || '').includes('Achat') ? 'bg-blue-100 text-blue-700' :
                          (pay.parent_category || '').includes('Charge') ? 'bg-red-100 text-red-700' :
                          (pay.parent_category || '').includes('Salaire') ? 'bg-orange-100 text-orange-700' :
                          'bg-gray-100 text-gray-700'
                        }`}>{pay.parent_category || '-'}</span>
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-700">{pay.supplier_name || '-'}</td>
                      <td className="px-4 py-4 text-sm font-bold text-gray-900 whitespace-nowrap">{fmt(invTotal)} DA</td>
                      <td className="px-4 py-4 text-sm font-semibold hidden md:table-cell" style={{ color: payAmount > 0 ? '#2E7D32' : '#999' }}>{fmt(payAmount)} DA</td>
                      <td className="px-4 py-4 text-sm font-semibold hidden md:table-cell" style={{ color: invRemaining > 0 ? '#C62828' : '#2E7D32' }}>{fmt(invRemaining)} DA</td>
                      <td className="px-4 py-4 text-sm text-gray-600 hidden sm:table-cell">{pay.date ? format(parseSafeDate(pay.date), 'dd MMM yyyy', { locale: fr }) : (pay.invoice_due_date ? format(new Date(pay.invoice_due_date), 'dd MMM yyyy', { locale: fr }) : '-')}</td>
                      <td className="px-4 py-4">
                        {invStatus === 'paid' ? (
                          <span className="inline-flex items-center gap-1.5 text-xs font-semibold" style={{ color: '#2E7D32' }}><span className="w-2 h-2 rounded-full" style={{ backgroundColor: '#4CAF50' }} /> Payée</span>
                        ) : invStatus === 'partial' ? (
                          <span className="inline-flex items-center gap-1.5 text-xs font-semibold" style={{ color: '#E65100' }}><span className="w-2 h-2 rounded-full" style={{ backgroundColor: '#FF9800' }} /> Partielle</span>
                        ) : (
                          <span className="inline-flex items-center gap-1.5 text-xs font-semibold" style={{ color: '#E65100' }}><span className="w-2 h-2 rounded-full" style={{ backgroundColor: '#FF9800' }} /> Non payé</span>
                        )}
                      </td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-1">
                          {canUpdate('factures_bl') && (
                            <button onClick={() => handleEditPayment(pay)} className="w-8 h-8 rounded-lg border border-gray-200 flex items-center justify-center text-gray-500 hover:bg-gray-50" title="Modifier"><Pencil className="w-3.5 h-3.5" /></button>
                          )}
                          {canDelete('factures_bl') && (
                            <button onClick={() => handleDeletePayment(pay)} className="w-8 h-8 rounded-lg border border-red-200 flex items-center justify-center text-red-400 hover:bg-red-50" title="Supprimer"><Trash2 className="w-3.5 h-3.5" /></button>
                          )}
                          {invStatus === 'paid' ? (
                            <span className="px-2 py-1 rounded text-xs font-semibold" style={{ backgroundColor: '#E8F5E9', color: '#2E7D32' }}>Payé</span>
                          ) : invStatus === 'partial' ? (
                            <span className="px-2 py-1 rounded text-xs font-semibold" style={{ backgroundColor: '#FFF3E0', color: '#E65100' }}>Partiel</span>
                          ) : (
                            <span className="px-2 py-1 rounded text-xs font-semibold" style={{ backgroundColor: '#FFF3E0', color: '#E65100' }}>Non payé</span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-5 py-3 border-t border-gray-100 bg-gray-50/50">
          <span className="text-xs text-gray-500">
            {activeItems.length} {activeTab === 'factures' ? 'factures' : activeTab === 'bls' ? 'bons de livraison' : 'paiements'}
          </span>
        </div>
      </div>

      {/* Bottom Row: Flux Trésorerie + Bouton */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Flux Trésorerie Mensuel */}
        <div className="bg-white rounded-2xl p-5 border border-gray-100" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-4">Flux trésorerie mensuel</h3>
          {(() => {
            const months = [
              { label: 'Mois -2', amount: Math.round((kpi.total_invoiced || 0) * 1.3), color: '#4CAF50' },
              { label: 'Mois -1', amount: Math.round((kpi.total_invoiced || 0) * 1.05), color: '#FF9800' },
              { label: 'Ce mois', amount: kpi.total_invoiced || 0, color: '#1976D2' },
            ];
            const maxAmount = Math.max(...months.map(m => m.amount), 1);
            return (
              <div className="space-y-3">
                {months.map((m, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <span className="text-sm text-gray-600 w-16">{m.label}</span>
                    <div className="flex-1 h-3 rounded-full" style={{ backgroundColor: '#F0F0EC' }}>
                      <div className="h-full rounded-full transition-all" style={{ width: `${(m.amount / maxAmount) * 100}%`, backgroundColor: m.color }} />
                    </div>
                    <span className="text-sm font-bold text-gray-700 w-20 text-right">{fmt(m.amount)} DA</span>
                  </div>
                ))}
              </div>
            );
          })()}
          <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
            <span className="text-sm text-gray-600">Dette en cours</span>
            <span className="text-lg font-bold" style={{ color: '#C62828' }}>{fmt(kpi.debt)} DA</span>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-2xl p-5 border border-gray-100" style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.04)' }}>
          <h3 className="text-sm font-bold text-gray-500 uppercase tracking-wide mb-4">Actions rapides</h3>
          <div className="space-y-2">
            {[
              { label: 'Factures en retard ce mois', icon: AlertCircle, color: '#C62828', onClick: () => openOverdueModal(true), testId: 'qa-overdue-this-month' },
              { label: 'Prévision trésorerie 30j', icon: TrendingUp, color: '#1976D2' },
              { label: 'Optimiser le taux de règlement', icon: ArrowUpRight, color: '#4CAF50' },
            ].map((action, i) => (
              <button
                key={i}
                onClick={action.onClick}
                disabled={!action.onClick}
                data-testid={action.testId}
                className="w-full flex items-center justify-between p-3 rounded-xl border border-gray-100 hover:bg-gray-50 transition-colors group disabled:opacity-60 disabled:cursor-not-allowed"
              >
                <div className="flex items-center gap-3">
                  <action.icon className="w-4 h-4" style={{ color: action.color }} />
                  <span className="text-sm text-gray-700">{action.label}</span>
                </div>
                <ArrowUpRight className="w-4 h-4 text-gray-400 group-hover:text-gray-600" />
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Invoice Wizard */}
      <InvoiceWizard open={wizardOpen} onClose={() => { setWizardOpen(false); setEditInvoice(null); }} onSuccess={fetchData} currentUser={currentUser} editData={editInvoice} />

      {/* Payment Modal */}
      {payModalOpen && payTarget && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 backdrop-blur-sm fade-in" onClick={() => setPayModalOpen(false)}>
          <div className="bg-white rounded-2xl w-full max-w-md overflow-hidden shadow-2xl border border-gray-100" onClick={e => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-emerald-600 to-teal-700 p-5 text-white flex justify-between items-center">
              <div>
                <h3 className="text-lg font-bold">
                  {(payTarget.paid_amount || 0) > 0 ? 'Compléter le paiement' : 'Enregistrer un paiement'}
                </h3>
                <p className="text-emerald-100 text-xs mt-1">Facture / BL</p>
              </div>
              <button onClick={() => setPayModalOpen(false)} className="text-emerald-100 hover:text-white bg-white/10 hover:bg-white/20 p-2 rounded-xl transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-5">
              <div className="rounded-xl p-4 bg-emerald-50/50 border border-emerald-100 text-sm space-y-2">
                <div className="flex justify-between"><span className="text-gray-500 font-medium">Facture/BL</span><span className="font-bold text-gray-900">{payTarget.invoice_number || payTarget.bl_number || payTarget.id?.slice(0,8)}</span></div>
                <div className="flex justify-between"><span className="text-gray-500 font-medium">Fournisseur</span><span className="font-bold text-gray-900">{payTarget.supplier_name}</span></div>
                <div className="flex justify-between pt-2 border-t border-emerald-100/50"><span className="text-gray-500 font-medium">Total TTC</span><span className="font-bold text-gray-900">{fmt(payTarget.amount)} DA</span></div>
                <div className="flex justify-between"><span className="text-gray-500 font-medium">Déjà payé</span><span className="font-bold text-emerald-700">{fmt(payTarget.paid_amount || 0)} DA</span></div>
                <div className="flex justify-between"><span className="text-gray-500 font-medium">Reste à payer</span><span className="font-bold text-red-600">{fmt(Math.max((payTarget.amount || 0) - (payTarget.paid_amount || 0), 0))} DA</span></div>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="text-xs font-bold text-gray-600 uppercase tracking-wider">Montant à payer (DA)</label>
                  <Input type="number" value={payForm.amount} onChange={e => setPayForm(f => ({...f, amount: e.target.value}))} className="mt-1 h-11 rounded-xl bg-gray-50 border-gray-200" />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs font-bold text-gray-600 uppercase tracking-wider">Date</label>
                    <Input type="date" value={payForm.date} onChange={e => setPayForm(f => ({...f, date: e.target.value}))} className="mt-1 h-11 rounded-xl bg-gray-50 border-gray-200" />
                  </div>
                  <div>
                    <label className="text-xs font-bold text-gray-600 uppercase tracking-wider">Mode</label>
                    <select value={payForm.payment_method} onChange={e => setPayForm(f => ({...f, payment_method: e.target.value}))} className="mt-1 h-11 w-full px-3 border border-gray-200 rounded-xl text-sm bg-gray-50 focus:ring-2 focus:ring-emerald-500/20 focus:border-emerald-500 outline-none transition-all">
                      <option value="cash">Espèces</option>
                      <option value="check">Chèque</option>
                      <option value="transfer">Virement</option>
                      <option value="card">Carte</option>
                    </select>
                  </div>
                </div>
                
                <div>
                  <label className="text-xs font-bold text-gray-600 uppercase tracking-wider">Note (optionnel)</label>
                  <Input value={payForm.notes} onChange={e => setPayForm(f => ({...f, notes: e.target.value}))} placeholder="Ex: Paiement partiel, numéro de chèque..." className="mt-1 h-11 rounded-xl bg-gray-50 border-gray-200" />
                </div>
              </div>

              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => setPayModalOpen(false)} className="flex-1 rounded-xl h-11 font-semibold text-gray-600 border-gray-200 hover:bg-gray-50">Annuler</Button>
                <Button onClick={submitPayment} className="flex-1 rounded-xl h-11 font-semibold bg-gradient-to-r from-emerald-600 to-teal-700 hover:from-emerald-700 hover:to-teal-800 text-white border-0 shadow-md hover:shadow-lg transition-all">
                  <Check className="w-4 h-4 mr-2" /> Valider
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Overdue Invoices Modal */}
      {overdueOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setOverdueOpen(false)}
          data-testid="overdue-modal-backdrop"
        >
          <div
            className="bg-white rounded-2xl w-full max-w-4xl max-h-[85vh] overflow-hidden flex flex-col shadow-2xl"
            onClick={e => e.stopPropagation()}
            data-testid="overdue-modal"
          >
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between" style={{ backgroundColor: '#FEF2F2' }}>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: '#C62828' }}>
                  <AlertCircle className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Factures en retard</h3>
                  <p className="text-xs text-gray-500">
                    {overdueLoading ? 'Chargement...' :
                     overdueData ? `${overdueData.count} facture${overdueData.count > 1 ? 's' : ''} • Reste à payer : ${fmt(overdueData.total_remaining)} DA` :
                     'Aucune donnée'}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setOverdueOpen(false)}
                className="text-gray-400 hover:text-gray-700 text-2xl leading-none"
                data-testid="overdue-modal-close"
              >×</button>
            </div>

            {/* Period toggle */}
            <div className="px-6 py-3 border-b border-gray-100 flex items-center gap-2 bg-gray-50">
              <button
                onClick={() => { setOverdueThisMonth(true); fetchOverdue(true); }}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${overdueThisMonth ? 'text-white' : 'bg-white text-gray-600 border border-gray-200'}`}
                style={overdueThisMonth ? { backgroundColor: '#C62828' } : {}}
                data-testid="overdue-filter-this-month"
              >Ce mois</button>
              <button
                onClick={() => { setOverdueThisMonth(false); fetchOverdue(false); }}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${!overdueThisMonth ? 'text-white' : 'bg-white text-gray-600 border border-gray-200'}`}
                style={!overdueThisMonth ? { backgroundColor: '#C62828' } : {}}
                data-testid="overdue-filter-all"
              >Toutes</button>
            </div>

            {/* List */}
            <div className="flex-1 overflow-auto" data-testid="overdue-list">
              {overdueLoading ? (
                <div className="p-8 text-center text-gray-500 text-sm">Chargement...</div>
              ) : !overdueData || overdueData.count === 0 ? (
                <div className="p-12 text-center">
                  <div className="w-14 h-14 rounded-full bg-green-50 flex items-center justify-center mx-auto mb-3">
                    <Check className="w-7 h-7 text-green-600" />
                  </div>
                  <p className="text-sm font-semibold text-gray-700">Aucune facture en retard</p>
                  <p className="text-xs text-gray-500 mt-1">{overdueThisMonth ? 'Pour le mois en cours.' : 'Toutes vos factures sont à jour.'}</p>
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wide">Fournisseur</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wide">N° Facture</th>
                      <th className="px-4 py-3 text-right text-xs font-bold text-gray-600 uppercase tracking-wide">Montant</th>
                      <th className="px-4 py-3 text-right text-xs font-bold text-gray-600 uppercase tracking-wide">Reste</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-gray-600 uppercase tracking-wide">Échéance</th>
                      <th className="px-4 py-3 text-center text-xs font-bold text-gray-600 uppercase tracking-wide">Retard</th>
                    </tr>
                  </thead>
                  <tbody>
                    {overdueData.items.map((inv) => (
                      <tr key={inv.id} className="border-t border-gray-100 hover:bg-red-50/40" data-testid={`overdue-row-${inv.id}`}>
                        <td className="px-4 py-3 font-medium text-gray-900">{inv.supplier_name || '—'}</td>
                        <td className="px-4 py-3 text-gray-700">{inv.invoice_number || inv.bl_number || inv.id?.slice(0, 8)}</td>
                        <td className="px-4 py-3 text-right font-semibold text-gray-900">{fmt(inv.amount)} DA</td>
                        <td className="px-4 py-3 text-right font-bold" style={{ color: '#C62828' }}>{fmt(inv.remaining_amount)} DA</td>
                        <td className="px-4 py-3 text-gray-700">
                          {inv.due_date ? format(new Date(inv.due_date), 'dd MMM yyyy', { locale: fr }) : '—'}
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-bold" style={{ backgroundColor: '#FEE2E2', color: '#C62828' }}>
                            {inv.days_overdue}j
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>

            {/* Footer */}
            {overdueData && overdueData.count > 0 && (
              <div className="px-6 py-3 border-t border-gray-100 bg-gray-50 flex items-center justify-between">
                <span className="text-xs text-gray-500">{overdueData.count} facture{overdueData.count > 1 ? 's' : ''} affichée{overdueData.count > 1 ? 's' : ''}</span>
                <span className="text-sm font-bold" style={{ color: '#C62828' }}>Total reste : {fmt(overdueData.total_remaining)} DA</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default FacturesBL;
